import { createAsyncThunk } from '@reduxjs/toolkit';

import type {
  ProjectCreateCommand,
  ProjectCreateResponse,
  ProjectGetCommand,
  ProjectIdCheckCommand,
  ProjectIdCheckResponse,
  ProjectImageGetCommand,
  ProjectImageGetResponse,
  ProjectImageSearchCommand,
  ProjectImageSearchResponse,
  ProjectImageUploadCommand,
  ProjectImageUploadResponse,
  ProjectItem,
  ProjectPostCreateCommand,
  ProjectPostCreateResponse,
  ProjectPostDeleteCommand,
  ProjectPostDeleteResponse,
  ProjectPostGetCommand,
  ProjectPostGetResponse,
  ProjectPostSearchCommand,
  ProjectPostSearchResponse,
  ProjectPostUpdateCommand,
  ProjectPostUpdateResponse,
  ProjectsWithdrawSearchRequest,
  ProjectsWithdrawSearchResponse,
  ProjectsSearchCommand,
  ProjectsSearchResponse,
  WithdrawCreateRequest,
  WithdrawGetRequest,
  WithdrawGetResponse,
  WithdrawPutRequest,
  WithdrawPutResponse,
  WithdrawSearchRequest,
  WithdrawSearchResponse,
  ProjectUpdateCommand,
  ProjectUpdateResponse,
} from '../Domain/entity/ProjectTypes';
import { projectUseCases } from './projectUseCases';

export const searchProjects = createAsyncThunk<
  ProjectsSearchResponse,
  ProjectsSearchCommand,
  { rejectValue: string }
>('project/searchProjects', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.searchProjects.execute(command);
    // 200 또는 0인 경우 성공으로 간주
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      return rejectWithValue(String((res as any).result_message || '프로젝트 검색 실패'));
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '프로젝트 검색 실패');
  }
});

export const searchWithdrawProjects = createAsyncThunk<
  ProjectsWithdrawSearchResponse,
  ProjectsWithdrawSearchRequest,
  { rejectValue: string }
>('project/searchWithdrawProjects', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.searchWithdrawProjects.execute(command);
    if ((res as any).statusCode !== undefined && (res as any).statusCode !== 200 && (res as any).statusCode !== 0) {
      return rejectWithValue(String((res as any).result_message || '프로젝트 검색 실패'));
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '프로젝트 검색 실패');
  }
});

export const getProject = createAsyncThunk<ProjectItem, ProjectGetCommand, { rejectValue: string }>(
  'project/getProject',
  async (command, { rejectWithValue }) => {
    try {
      const res = await projectUseCases.getProject.execute(command);
      const resAny = res as any;
      if (resAny.statusCode !== undefined && resAny.statusCode !== 200 && resAny.statusCode !== 0) {
        return rejectWithValue(String(resAny.result_message || '프로젝트 조회 실패'));
      }
      return res;
    } catch (error) {
      return rejectWithValue((error as Error).message ?? '프로젝트 조회 실패');
    }
  },
);

export const checkProjectId = createAsyncThunk<ProjectIdCheckResponse, ProjectIdCheckCommand, { rejectValue: string }>(
  'project/checkProjectId',
  async (command, { rejectWithValue }) => {
    try {
      const res = await projectUseCases.checkProjectId.execute(command);
      const resAny = res as any;
      if (resAny.statusCode !== undefined && resAny.statusCode !== 200 && resAny.statusCode !== 0) {
        return rejectWithValue(String(resAny.result_message || '프로젝트 ID 중복 확인 실패'));
      }
      return res;
    } catch (error) {
      return rejectWithValue((error as Error).message ?? '프로젝트 ID 중복 확인 실패');
    }
  },
);

export const uploadProjectImage = createAsyncThunk<
  ProjectImageUploadResponse,
  ProjectImageUploadCommand,
  { rejectValue: string }
>('project/uploadProjectImage', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.uploadProjectImage.execute(command);
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      return rejectWithValue(String(res.result_message || '이미지 업로드 실패'));
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '이미지 업로드 실패');
  }
});

export const getProjectImage = createAsyncThunk<
  ProjectImageGetResponse,
  ProjectImageGetCommand,
  { rejectValue: string }
>('project/getProjectImage', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.getProjectImage.execute(command);
    // getProjectImage는 statusCode가 따로 정의되지 않았을 수 있으므로 null 체크
    const resAny = res as any;
    if (resAny.statusCode !== undefined && resAny.statusCode !== 200 && resAny.statusCode !== 0) {
      return rejectWithValue(String(resAny.result_message || '이미지 조회 실패'));
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '이미지 조회 실패');
  }
});

export const searchProjectImages = createAsyncThunk<
  ProjectImageSearchResponse,
  ProjectImageSearchCommand,
  { rejectValue: string }
>('project/searchProjectImages', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.searchProjectImages.execute(command);
    const resAny = res as any;
    if (resAny.statusCode !== undefined && resAny.statusCode !== 200 && resAny.statusCode !== 0) {
      return rejectWithValue(String(resAny.result_message || '이미지 검색 실패'));
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '이미지 검색 실패');
  }
});

export const createProject = createAsyncThunk<
  ProjectCreateResponse,
  ProjectCreateCommand,
  { rejectValue: string }
>('project/createProject', async (command, { rejectWithValue }) => {
  try {
    const hasVideoUrl = String(command.video_url ?? '').trim().length > 0;
    const normalizedCommand: ProjectCreateCommand = {
      ...command,
      is_image_display: hasVideoUrl ? 'false' : 'true',
    };
    const res = await projectUseCases.createProject.execute(normalizedCommand);
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      const msg = typeof res.result_message === 'string' 
        ? res.result_message 
        : (res as any).result_message?.message || JSON.stringify(res.result_message) || '프로젝트 생성 실패';
      return rejectWithValue(msg);
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '프로젝트 생성 실패');
  }
});

export const updateProject = createAsyncThunk<
  ProjectUpdateResponse,
  ProjectUpdateCommand,
  { rejectValue: string }
>('project/updateProject', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.updateProject.execute(command);
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      const msg = typeof res.result_message === 'string' 
        ? res.result_message 
        : (res as any).result_message?.message || JSON.stringify(res.result_message) || '프로젝트 저장 실패';
      return rejectWithValue(msg);
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '프로젝트 저장 실패');
  }
});

export const searchProjectPosts = createAsyncThunk<
  ProjectPostSearchResponse,
  ProjectPostSearchCommand,
  { rejectValue: string }
>('project/searchProjectPosts', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.searchProjectPosts.execute(command);
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      return rejectWithValue(String((res as any).result_message || '소식 검색 실패'));
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '소식 검색 실패');
  }
});

export const getProjectPost = createAsyncThunk<
  ProjectPostGetResponse,
  ProjectPostGetCommand,
  { rejectValue: string }
>('project/getProjectPost', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.getProjectPost.execute(command);
    const resAny = res as any;
    if (resAny.statusCode !== undefined && resAny.statusCode !== 200 && resAny.statusCode !== 0) {
      return rejectWithValue(String(resAny.result_message || '소식 조회 실패'));
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '소식 조회 실패');
  }
});

export const createProjectPost = createAsyncThunk<
  ProjectPostCreateResponse,
  ProjectPostCreateCommand,
  { rejectValue: string }
>('project/createProjectPost', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.createProjectPost.execute(command);
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      const msg = typeof res.result_message === 'string'
        ? res.result_message
        : (res as any).result_message?.message || JSON.stringify(res.result_message) || '소식 등록 실패';
      return rejectWithValue(msg);
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '소식 등록 실패');
  }
});

export const updateProjectPost = createAsyncThunk<
  ProjectPostUpdateResponse,
  ProjectPostUpdateCommand,
  { rejectValue: string }
>('project/updateProjectPost', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.updateProjectPost.execute(command);
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      const msg = typeof res.result_message === 'string'
        ? res.result_message
        : (res as any).result_message?.message || JSON.stringify(res.result_message) || '소식 수정 실패';
      return rejectWithValue(msg);
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '소식 수정 실패');
  }
});

export const deleteProjectPost = createAsyncThunk<
  ProjectPostDeleteResponse,
  ProjectPostDeleteCommand,
  { rejectValue: string }
>('project/deleteProjectPost', async (command, { rejectWithValue }) => {
  try {
    const res = await projectUseCases.deleteProjectPost.execute(command);
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      const msg = typeof res.result_message === 'string'
        ? res.result_message
        : (res as any).result_message?.message || JSON.stringify(res.result_message) || '소식 삭제 실패';
      return rejectWithValue(msg);
    }
    return res;
  } catch (error) {
    return rejectWithValue((error as Error).message ?? '소식 삭제 실패');
  }
});

export const createWithdraw = createAsyncThunk<unknown, WithdrawCreateRequest, { rejectValue: string }>(
  'project/createWithdraw',
  async (body, { rejectWithValue }) => {
    try {
      return await projectUseCases.createWithdraw.execute(body);
    } catch (error) {
      return rejectWithValue((error as Error).message ?? '지급요청 실패');
    }
  },
);

export const searchWithdraw = createAsyncThunk<WithdrawSearchResponse, WithdrawSearchRequest, { rejectValue: string }>(
  'project/searchWithdraw',
  async (body, { rejectWithValue }) => {
    try {
      return await projectUseCases.searchWithdraw.execute(body);
    } catch (error) {
      return rejectWithValue((error as Error).message ?? '지급요청 조회 실패');
    }
  },
);

export const getWithdraw = createAsyncThunk<WithdrawGetResponse, WithdrawGetRequest, { rejectValue: string }>(
  'project/getWithdraw',
  async (body, { rejectWithValue }) => {
    try {
      return await projectUseCases.getWithdraw.execute(body);
    } catch (error) {
      return rejectWithValue((error as Error).message ?? '지급요청 상세 조회 실패');
    }
  },
);

export const updateWithdraw = createAsyncThunk<WithdrawPutResponse, WithdrawPutRequest, { rejectValue: string }>(
  'project/updateWithdraw',
  async (body, { rejectWithValue }) => {
    try {
      return await projectUseCases.updateWithdraw.execute(body);
    } catch (error) {
      return rejectWithValue((error as Error).message ?? '지급요청 저장 실패');
    }
  },
);

